import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  getPartes, getColumnasPartes, getProyectosUnidades, partesApi,
  createColumna, updateColumna, deleteColumna, renombrarOpcion, exportPartesExcel,
} from '../../api/client'
import TableFilters from '../TableFilters'
import PlanillaCell from '../unidades/PlanillaCells'
import PlanillaFiltros from '../unidades/PlanillaFiltros'
import { AddColumnaModal, ColumnaMenu, EncabezadoFijo } from '../unidades/PlanillaColumnas'
import { ordenar, siguienteOrden } from '../unidades/ordenLogica'
import ArchivosModal from '../unidades/ArchivosModal'
import { aplicarFiltros, columnasFiltrables, filtroCompleto, valorDe } from '../unidades/filtrosLogica'
import FiltroColumna from './FiltroColumna'
import { TIPO_ICON, formatFecha, moverClave } from '../unidades/tipos'
import { cumpleFiltros } from './filtroPartes'
import { agruparPartes } from './agruparPartes'
import { usePuedeEditarMantenimiento, usePuedeAbrirPartes } from '../../utils/mantenimiento'

// Grouping the table (remembered in this browser): by the board column
// "Ubicación del parte" (its groups in the column's option order), by unit or
// by state. Groups fold with a click, also remembered.
const UBICACION = 'Ubicación del parte'
const AGRUPAR = ['', 'ubicacion', 'unidad', 'estado']
const ORDEN_ESTADO = ['Abierto', 'Cerrado', 'Cancelado']
const COLORES = ['#534AB7', '#0F6E56', '#185FA5', '#993C1D', '#854F0B', '#993556', '#3B6D11', '#5F5E5A']
const leer = (k, def) => { try { return JSON.parse(localStorage.getItem(k)) ?? def } catch { return def } }
const guardar = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)) } catch { /* private mode */ } }

// A parte's own fields, as the column filters see them.
const CAMPOS_PARTE = [
  { id: 'numero', tipo: 'numero' },
  { id: 'nombre', tipo: 'texto' },
  { id: 'unidad_codigo', tipo: 'texto' },
  { id: 'fecha_apertura', tipo: 'fecha' },
  { id: 'fecha_cierre', tipo: 'fecha' },
  { id: 'fs', tipo: 'estado', opciones: ['F/S', 'No'] },   // from implica_fs, see `rows`
]

// The Partes board: every parte with its fixed fields plus one column per
// board column (ambito "partes"), edited, filtered and exported exactly like
// the units Planilla. `actualizados` ({id: parte}): partes opened, edited or
// closed elsewhere (the page's dialogs, the panel) -- merged in as shown, new
// ones on top. `onOpenParte(id)` opens its panel; `onCerrar(parte)` asks to
// close it. Read-only for those who may not edit Mantenimiento.
export default function PartesTabla({ actualizados = {}, onOpenParte, onCerrar }) {
  const { t } = useTranslation('engineering')
  const puedeEditar = usePuedeEditarMantenimiento()
  const puedeCerrar = usePuedeAbrirPartes()      // the row's Cerrar: also whoever holds crear_ot_mantenimiento
  const navigate = useNavigate()
  const [proyectos, setProyectos] = useState([])
  const [cargados, setRows] = useState([])
  const [params, setParams] = useState({})         // the server filters of the last load
  const [columnas, setColumnas] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [cellError, setCellError] = useState('')
  const [adding, setAdding] = useState(false)
  const [menu, setMenu] = useState(null)
  const [archivosDe, setArchivosDe] = useState(null)
  const [filtros, setFiltros] = useState([])
  const [verFiltros, setVerFiltros] = useState(false)
  const [porColumna, setPorColumna] = useState({})        // { column id: { op, valor } }: the filters set from the headers
  const [seleccion, setSeleccion] = useState(() => new Set())
  const [exportando, setExportando] = useState(false)
  // Row order, by one column (ordenLogica.js); null = newest number first, as loaded.
  const [orden, setOrden] = useState(null)
  const ordenarPor = (campo, dir) => setOrden(o => siguienteOrden(o, campo, dir))
  const requestId = useRef(0)
  const [agrupar, setAgrupar] = useState(() => { const v = leer('partes_agrupar', ''); return AGRUPAR.includes(v) ? v : '' })
  const [plegados, setPlegados] = useState(() => new Set(leer('partes_grupos_plegados', [])))
  const elegirAgrupar = (v) => { setAgrupar(v); guardar('partes_agrupar', v) }
  const plegar = (clave) => setPlegados(ps => {
    const next = new Set(ps)
    next.has(clave) ? next.delete(clave) : next.add(clave)
    guardar('partes_grupos_plegados', [...next])
    return next
  })

  useEffect(() => { getProyectosUnidades().then(setProyectos).catch(() => {}) }, [])

  const load = async (p) => {
    const id = ++requestId.current
    setParams(p || {})
    setCellError('')
    setMenu(null)
    try {
      const [partes, cols] = await Promise.all([
        getPartes({ ...p, incluir_atributos: true }),
        getColumnasPartes(),
      ])
      if (id !== requestId.current) return
      setRows(partes)
      setColumnas(cols)
      setLoadError('')
    } catch {
      if (id === requestId.current) setLoadError(t('partes.loadFailed'))
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }

  const onFiltersChange = (p) => load(p)

  // What's loaded, with what changed elsewhere laid over it: a parte's own
  // fields from `actualizados` (its board cells stay the ones edited here),
  // and partes opened since the load added on top.
  const rows = useMemo(() => {
    const vistos = new Set(cargados.map(r => r.id))
    const nuevos = Object.values(actualizados).filter(p => !vistos.has(p.id))
      .map(p => ({ ...p, atributos: p.atributos || {}, archivos: p.archivos || {} }))
      .sort((a, b) => b.numero - a.numero)
    const fijos = ({ atributos, archivos, ...resto }) => resto   // eslint-disable-line no-unused-vars
    return [...nuevos, ...cargados.map(r => actualizados[r.id] ? { ...r, ...fijos(actualizados[r.id]) } : r)]
      // What changed here still answers to the filters: a parte cancelled or
      // closed while "Abiertos" is filtered leaves the list.
      .filter(r => cumpleFiltros(r, params))
      // Text forms of implica_fs and open/closed, to filter and sort by.
      .map(r => ({ ...r, fs: r.implica_fs ? 'F/S' : 'No', estado_txt: r.abierto ? 'Abierto' : r.cancelado ? 'Cancelado' : 'Cerrado' }))
  }, [cargados, actualizados, params])

  const filtrables = useMemo(() => columnasFiltrables(columnas, {
    numero: t('partes.campos.numero'), nombre: t('partes.campos.nombre'), unidad_codigo: t('partes.campos.unidad'),
    fecha_apertura: t('partes.campos.fecha_apertura'), fecha_cierre: t('partes.campos.fecha_cierre'),
    fs: t('partes.campos.implica_fs'),
  }, [], CAMPOS_PARTE), [columnas, t])
  const filtrosColumna = useMemo(() => Object.entries(porColumna).map(([campo, f]) => ({ campo, ...f })), [porColumna])
  const visibles = useMemo(() => aplicarFiltros(rows, [...filtros, ...filtrosColumna], filtrables), [rows, filtros, filtrosColumna, filtrables])
  const ordenadas = useMemo(() => ordenar(visibles, orden,
    [...filtrables, { id: 'estado_txt', tipo: 'texto', fijo: true }]), [visibles, orden, filtrables])
  const filtrosActivos = filtros.filter(filtroCompleto).length + filtrosColumna.length
  const limpiarFiltros = () => { setFiltros([]); setPorColumna({}) }
  // A header's filter: the column (id, type, name) and what it shows in the rows now.
  const filtroDe = (id, tipo, nombre) => {
    const col = filtrables.find(c => String(c.id) === String(id)) || { id, tipo }
    return (
      <FiltroColumna columna={{ id, tipo, nombre, opciones: col.opciones }} valores={rows.map(r => valorDe(r, { ...col, tipo: tipo === 'lista' ? 'texto' : col.tipo }))}
        filtro={porColumna[id]} t={t}
        onChange={f => setPorColumna(m => { const n = { ...m }; if (f) n[id] = f; else delete n[id]; return n })} />
    )
  }
  const colUbicacion = columnas.find(c => c.nombre === UBICACION)
  const modo = agrupar === 'ubicacion' && !colUbicacion ? '' : agrupar
  const grupos = useMemo(() => {
    if (modo === 'ubicacion') return agruparPartes(ordenadas, r => r.atributos?.[UBICACION], colUbicacion?.opciones || [])
    if (modo === 'unidad') return agruparPartes(ordenadas, r => r.unidad_codigo)
    if (modo === 'estado') return agruparPartes(ordenadas, r => r.estado_txt, ORDEN_ESTADO)
    return null
  }, [modo, ordenadas, colUbicacion])
  const nombreGrupo = (clave) => !clave ? t(`partes.agrupar.sin.${modo}`)
    : modo === 'estado' ? t({ Abierto: 'partes.abierto', Cerrado: 'partes.cerrado', Cancelado: 'partes.cancelado' }[clave] || clave)
    : clave

  const todasVisibles = visibles.length > 0 && visibles.every(u => seleccion.has(u.id))
  const algunaVisible = visibles.some(u => seleccion.has(u.id))
  const toggleTodas = () => setSeleccion(s => {
    const next = new Set(s)
    visibles.forEach(u => todasVisibles ? next.delete(u.id) : next.add(u.id))
    return next
  })
  const toggleUna = (id) => setSeleccion(s => {
    const next = new Set(s)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const seleccionadas = rows.filter(u => seleccion.has(u.id))

  const descargar = async () => {
    setExportando(true)
    try { await exportPartesExcel(seleccionadas.map(u => u.id)) }
    catch { setCellError(t('unidades.filtros.exportFailed')) }
    setExportando(false)
  }

  // A parte opened after the load lives only in `actualizados`: its first
  // edited cell brings it into the loaded rows.
  const patchRow = (id, fn) => {
    const actual = rows.find(r => r.id === id)
    setRows(rs => rs.some(r => r.id === id) ? rs.map(r => r.id === id ? fn(r) : r) : (actual ? [fn(actual), ...rs] : rs))
  }
  const replaceColumna = (c) => setColumnas(cs => cs.map(x => x.id === c.id ? c : x))

  const saveCell = async (parte, columna, valor) => {
    try {
      const saved = await partesApi.setAtributo(parte.id, columna.nombre, valor)
      patchRow(parte.id, r => ({ ...r, atributos: { ...(r.atributos || {}), [columna.nombre]: saved.valor } }))
      setCellError('')
    } catch (e) {
      setCellError(t('partes.saveCellFailed', { numero: parte.numero, nombre: columna.nombre }))
      throw e
    }
  }

  const addOpcion = async (columna, nombre) => {
    const existing = (columna.opciones || []).find(o => o.toLowerCase() === nombre.toLowerCase())
    if (existing) return existing
    try {
      replaceColumna(await updateColumna(columna.id, { opciones: [...(columna.opciones || []), nombre] }))
      return nombre
    } catch {
      setCellError(t('unidades.planilla.columnSaveFailed'))
      return null
    }
  }

  const onUpdateColumna = async (columna, data) => {
    const c = await updateColumna(columna.id, data)
    replaceColumna(c)
    setMenu(m => m && { ...m, columna: c })
    // Renamed: the backend moved the saved values; move the loaded ones too.
    if (c.nombre !== columna.nombre) setRows(rs => rs.map(r => moverClave(r, columna.nombre, c.nombre)))
  }

  const onRenombrarOpcion = async (columna, de, a, fusionar) => {
    const { columna: c } = await renombrarOpcion(columna.id, de, a, fusionar)
    replaceColumna(c)
    setMenu(m => m && { ...m, columna: c })
    const final = fusionar ? c.opciones.find(o => o.toLowerCase() === a.toLowerCase()) : a.trim()
    setRows(rs => rs.map(r => r.atributos?.[columna.nombre] === de
      ? { ...r, atributos: { ...r.atributos, [columna.nombre]: final } } : r))
    setFiltros(fs => fs.map(f => String(f.campo) === String(columna.id) && f.valor === de ? { ...f, valor: final } : f))
  }

  const onDeleteColumna = async (columna) => {
    if (!confirm(t('unidades.planilla.deleteColumnConfirm', { nombre: columna.nombre, alcance: t('partes.deleteScope') }))) return
    try {
      await deleteColumna(columna.id)
      setMenu(null)
      setColumnas(cs => cs.filter(x => x.id !== columna.id))
      setFiltros(fs => fs.filter(f => String(f.campo) !== String(columna.id)))
      setRows(rs => rs.map(r => {
        const atributos = { ...(r.atributos || {}) }
        const archivos = { ...(r.archivos || {}) }
        delete atributos[columna.nombre]; delete archivos[columna.nombre]
        return { ...r, atributos, archivos }
      }))
    } catch {
      setCellError(t('unidades.planilla.columnSaveFailed'))
    }
  }

  // One parte's row.
  const fila = (p) => {
    const marcada = seleccion.has(p.id)
    const fondo = marcada ? 'bg-blue-50' : 'bg-white'
    return (
      <tr key={p.id} className={`border-b border-gray-50 last:border-b-0 ${marcada ? 'bg-blue-50' : 'hover:bg-gray-50/50'}`}>
        <td className={`w-10 min-w-[2.5rem] px-3 sticky left-0 z-10 ${fondo}`}>
          <input type="checkbox" checked={marcada} onChange={() => toggleUna(p.id)} aria-label={`#${p.numero}`} className="cursor-pointer" />
        </td>
        <td className={`px-3 py-2 font-medium text-gray-900 whitespace-nowrap sticky left-10 z-10 tabular-nums ${fondo}`}>
          <button type="button" onClick={() => onOpenParte(p.id)} className="hover:underline">#{p.numero}</button>
        </td>
        <td className="px-3 py-2 max-w-xs">
          <div className="flex items-center justify-between gap-2 w-full">
            <button type="button" onClick={() => onOpenParte(p.id)} title={p.nombre}
              className="text-left text-gray-900 hover:underline truncate block min-w-0 flex-1">{p.nombre}</button>
            {p.n_comentarios > 0 && (
              <button type="button" onClick={() => onOpenParte(p.id)} title={t('partes.comentarios', { count: p.n_comentarios })}
                aria-label={t('partes.comentarios', { count: p.n_comentarios })}
                className="relative shrink-0 text-gray-400 hover:text-gray-700 leading-none pr-2.5">
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.6-.7L3 21l1.9-5.2A8.4 8.4 0 0 1 3 11.5 8.5 8.5 0 0 1 12 3a8.5 8.5 0 0 1 9 8.5Z" />
                </svg>
                <sub className="absolute -bottom-1 right-0 text-[10px] font-bold text-red-600 tabular-nums">{p.n_comentarios}</sub>
              </button>
            )}
          </div>
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <button type="button" onClick={() => navigate(`/operations/unidades?unidad=${p.unidad_id}`)}
            title={t('partes.panel.openUnit')} className="font-medium text-gray-800 hover:underline">
            {p.unidad_codigo}
          </button>
          {p.unidad_proyecto && <span className="text-xs text-gray-400 ml-1">{p.unidad_proyecto}</span>}
        </td>
        <td className="px-3 py-2 text-gray-600 whitespace-nowrap tabular-nums">{formatFecha(p.fecha_apertura)}</td>
        <td className="px-3 py-2 whitespace-nowrap">
          {p.abierto ? (
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{t('partes.abierto')}</span>
              {puedeCerrar && (
                <button type="button" onClick={() => onCerrar(p)} className="text-xs font-medium text-gray-500 hover:text-gray-900">
                  {t('partes.cerrar.button')}
                </button>
              )}
            </span>
          ) : p.cancelado ? (
            <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600 line-through decoration-gray-400"
              title={[p.cancelado_por && t('partes.canceladoPor', { por: p.cancelado_por }), p.motivo_cancelacion].filter(Boolean).join(' · ')}>
              {t('partes.cancelado')}
            </span>
          ) : (
            <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
              title={p.cerrado_por ? t('partes.cerradoPor', { por: p.cerrado_por }) : undefined}>
              {t('partes.cerradoEl', { fecha: formatFecha(p.fecha_cierre) })}
            </span>
          )}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {p.implica_fs
            ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800" title={t('partes.fs.badgeHint')}>F/S</span>
            : <span className="text-xs text-gray-400">{t('partes.fs.no')}</span>}
        </td>
        {columnas.map(c => (
          <td key={c.id} className="px-1 py-1">
            <PlanillaCell columna={c}
              value={p.atributos?.[c.nombre] ?? null}
              archivos={p.archivos?.[c.nombre] || []}
              onSave={valor => saveCell(p, c, valor)}
              onAddOpcion={nombre => addOpcion(c, nombre)}
              onOpenArchivos={() => setArchivosDe({ parteId: p.id, columna: c })}
              soloLectura={!puedeEditar} />
          </td>
        ))}
      </tr>
    )
  }

  const th = 'text-left px-3 py-2.5 font-semibold text-gray-600 whitespace-nowrap'

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <TableFilters
          search={{ param: 'buscar', placeholder: t('partes.searchPlaceholder') }}
          filters={[
            { param: 'estado', label: t('partes.estado'), type: 'select',
              options: [{ value: 'abierto', label: t('partes.abiertos') }, { value: 'cerrado', label: t('partes.cerrados') },
                { value: 'cancelado', label: t('partes.cancelados') }] },
            { param: 'proyecto', label: t('unidades.proyecto'), type: 'select', options: proyectos },
          ]}
          onChange={onFiltersChange}
        />
        {puedeEditar && (
          <button type="button" onClick={() => setAdding(true)}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shrink-0">
            {t('unidades.planilla.addColumn')}
          </button>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mb-3 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => {
                  if (!verFiltros && filtros.length === 0 && filtrables.length) {
                    setFiltros([{ id: Date.now(), campo: 'nombre', op: 'contains', valor: '' }])
                  }
                  setVerFiltros(v => !v)
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                  filtrosActivos ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                {t('unidades.filtros.button')}{filtrosActivos ? ` · ${filtrosActivos}` : ''}
              </button>
              {filtrosActivos > 0 && (
                <>
                  <span className="text-xs text-gray-500">{t('partes.showing', { n: visibles.length, total: rows.length })}</span>
                  <button type="button" onClick={limpiarFiltros} className="text-xs font-medium text-red-600 hover:text-red-700">
                    {t('unidades.filtros.clearAll')}
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('partes.agrupar.titulo')}</span>
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                {AGRUPAR.filter(v => v !== 'ubicacion' || colUbicacion).map(v => (
                  <button key={v || 'no'} type="button" onClick={() => elegirAgrupar(v)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium ${modo === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                    {t(`partes.agrupar.por.${v || 'nada'}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {seleccionadas.length > 0 && (
                <>
                  <span className="text-xs text-gray-500">{t('partes.selected', { count: seleccionadas.length })}</span>
                  <button type="button" onClick={() => setSeleccion(new Set())} className="text-xs font-medium text-gray-500 hover:text-gray-900">
                    {t('unidades.filtros.clearSelection')}
                  </button>
                </>
              )}
              <button type="button" onClick={descargar} disabled={!seleccionadas.length || exportando}
                title={seleccionadas.length ? undefined : t('partes.selectFirst')}
                className="inline-flex items-center gap-1.5 bg-gray-900 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors">
                {exportando ? t('unidades.filtros.exporting') : t('unidades.filtros.export')}
              </button>
            </div>
          </div>
          {verFiltros && <PlanillaFiltros columnas={filtrables} filtros={filtros} onChange={setFiltros} />}
        </div>
      )}

      {cellError && <p className="text-red-500 text-xs mb-3">{cellError}</p>}

      {loadError ? (
        <div className="text-red-500 text-sm">{loadError}</div>
      ) : loading && rows.length === 0 ? (
        <div className="text-gray-400 text-sm">{t('unidades.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400"><p>{t('partes.empty')}</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="text-sm min-w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-10 min-w-[2.5rem] px-3 sticky left-0 bg-gray-50 z-10">
                  <input type="checkbox" checked={todasVisibles} aria-label={t('unidades.filtros.selectAll')}
                    ref={el => { if (el) el.indeterminate = !todasVisibles && algunaVisible }}
                    onChange={toggleTodas} className="cursor-pointer" />
                </th>
                <th className={`${th} sticky left-10 bg-gray-50 z-10`}><EncabezadoFijo label={t('partes.campos.numero')} campo="numero" tipo="numero" orden={orden} onOrdenar={ordenarPor} />{filtroDe('numero', 'numero', t('partes.campos.numero'))}</th>
                <th className={th}><EncabezadoFijo label={t('partes.campos.nombre')} campo="nombre" orden={orden} onOrdenar={ordenarPor} />{filtroDe('nombre', 'texto', t('partes.campos.nombre'))}</th>
                <th className={th}><EncabezadoFijo label={t('partes.campos.unidad')} campo="unidad_codigo" orden={orden} onOrdenar={ordenarPor} />{filtroDe('unidad_codigo', 'texto', t('partes.campos.unidad'))}</th>
                <th className={th}><EncabezadoFijo label={t('partes.campos.fecha_apertura')} campo="fecha_apertura" tipo="fecha" orden={orden} onOrdenar={ordenarPor} />{filtroDe('fecha_apertura', 'fecha', t('partes.campos.fecha_apertura'))}</th>
                <th className={th}><EncabezadoFijo label={t('partes.estado')} campo="estado_txt" orden={orden} onOrdenar={ordenarPor} />{filtroDe('estado_txt', 'lista', t('partes.estado'))}</th>
                <th className={th} title={t('partes.campos.implica_fs')}><EncabezadoFijo label="F/S" campo="fs" orden={orden} onOrdenar={ordenarPor} />{filtroDe('fs', 'lista', 'F/S')}</th>
                {columnas.map(c => (
                  <th key={c.id} className="px-1 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">
                    <button type="button" data-columna-btn
                      onClick={e => {
                        const r = e.currentTarget.getBoundingClientRect()
                        setMenu(m => m?.columna.id === c.id ? null : { columna: c, anchor: r })
                      }}
                      title={t(`unidades.planilla.tipos.${c.tipo}`)}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                        menu?.columna.id === c.id ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100'}`}>
                      <span className="text-[11px] text-gray-400 font-normal w-4 text-center">{TIPO_ICON[c.tipo]}</span>
                      <span>{c.nombre}</span>
                      {orden && String(orden.campo) === String(c.id) && <span className="text-[10px] text-gray-500 font-normal">{orden.dir === 'asc' ? '▲' : '▼'}</span>}
                      <span className="text-gray-300 font-normal">⋮</span>
                    </button>
                    {filtroDe(c.id, c.tipo, c.nombre)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 && (
                <tr><td colSpan={columnas.length + 7} className="px-3 py-10 text-center text-gray-400">
                  {t('unidades.filtros.noMatches')}{' '}
                  <button type="button" onClick={limpiarFiltros} className="underline hover:text-gray-700">{t('unidades.filtros.clearAll')}</button>
                </td></tr>
              )}
              {grupos ? grupos.map((g, i) => {
                const clave = `${modo}:${g.clave}`
                const plegado = plegados.has(clave)
                // A location keeps its color whatever the filters leave (its place among the options).
                const pos = modo === 'ubicacion' ? (colUbicacion.opciones || []).findIndex(o => o.toLowerCase() === g.clave.toLowerCase()) : -1
                const color = g.clave ? COLORES[(pos >= 0 ? pos : i) % COLORES.length] : '#B4B2A9'
                return [
                  <tr key={clave} className="border-b border-gray-100 bg-gray-50/70 cursor-pointer hover:bg-gray-100/70" onClick={() => plegar(clave)}>
                    <td colSpan={columnas.length + 7} className="px-0 py-0" style={{ borderLeft: `4px solid ${color}` }}>
                      <div className="sticky left-0 inline-flex items-center gap-2.5 px-3 py-2" aria-expanded={!plegado}>
                        <span className={`text-gray-400 text-xs transition-transform ${plegado ? '-rotate-90' : ''}`}>▼</span>
                        <span className="font-semibold text-gray-900">{nombreGrupo(g.clave)}</span>
                        <span className="text-xs text-gray-500">{t('partes.agrupar.nPartes', { count: g.filas.length })}</span>
                        {g.fs > 0 && <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800">{g.fs} F/S</span>}
                        <span className="ml-2 h-1.5 w-28 rounded-full bg-gray-200 overflow-hidden" title={t('partes.agrupar.barraFs', { fs: g.fs, n: g.filas.length })}>
                          <span className="block h-full bg-red-500" style={{ width: `${(100 * g.fs) / g.filas.length}%` }} />
                        </span>
                      </div>
                    </td>
                  </tr>,
                  ...(plegado ? [] : g.filas.map(fila)),
                ]
              }) : ordenadas.map(fila)}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && puedeEditar && <p className="text-xs text-gray-400 mt-2">{t('unidades.planilla.hint')}</p>}

      {menu && (
        <ColumnaMenu key={menu.columna.id} columna={menu.columna} anchor={menu.anchor}
          valoresEnUso={[...new Set(rows.map(r => r.atributos?.[menu.columna.nombre]).filter(Boolean))]}
          onClose={() => setMenu(null)}
          onUpdate={data => onUpdateColumna(menu.columna, data)}
          onRenombrar={(de, a, fusionar) => onRenombrarOpcion(menu.columna, de, a, fusionar)}
          orden={orden} onOrdenar={ordenarPor}
          onDelete={() => onDeleteColumna(menu.columna)}
          soloLectura={!puedeEditar} />
      )}
      {adding && (
        <AddColumnaModal ambito="partes" onClose={() => setAdding(false)}
          onCreate={async data => { const c = await createColumna(data); setColumnas(cs => [...cs, c]) }} />
      )}
      {archivosDe && (() => {
        const p = rows.find(r => r.id === archivosDe.parteId)
        if (!p) return null
        const nombre = archivosDe.columna.nombre
        return (
          <ArchivosModal unidad={{ id: p.id, codigo: `#${p.numero}` }} columna={archivosDe.columna} api={partesApi}
            archivos={p.archivos?.[nombre] || []}
            onChange={next => patchRow(p.id, r => ({ ...r, archivos: { ...(r.archivos || {}), [nombre]: next } }))}
            onClose={() => setArchivosDe(null)}
            soloLectura={!puedeEditar} />
        )
      })()}
    </div>
  )
}
